import { Box, FormControl, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import OTPInput from "react-otp-input";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { forgotPassword, otpVerify } from "../../stores/auth";
import { OtpVerifyPayload } from "../../stores/auth/Interface";
import { useAppDispatch } from "../../stores/store";
import { setToast } from "../../stores/toast/toastslice";
import theme from "../../theme";
import CustomButton from "../common-components/CustomButton";
import { useCountdownTimer } from "../hooks/useCountdownTimer";

const ResponsiveInput = styled("input")(({ theme }) => ({
  width: "66px !important",
  marginBottom: "30px",
  height: "58px",
  border: "none",
  backgroundColor: "white",
  borderRadius: "8px",
  color: "#2C3034",
  fontSize: "16px",
  fontWeight: 500,
  textAlign: "center",
  outline: "none",
  [theme.breakpoints.down("sm")]: {
    width: "50px !important",
  },
}));

const otpVerifySchema = Yup.object({
  otp: Yup.number()
    .typeError("OTP must be a number.")
    .test(
      "length",
      "OTP must be exactly 4 digits.",
      (value) => value?.toString().length === 4
    )
    .required("OTP is required."),
});

const OtpVerificationForm = () => {
  const [otp, setOtp] = useState<string>("");

  const [email, setEmail] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // regex to detect email inside text
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

  const initialTime = 120;
  const { timer, startTimer, resetTimer, stopTimer } =
    useCountdownTimer(initialTime);

  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const expiry = localStorage.getItem("otp_expiry");

    if (expiry) {
      const remaining = Number(expiry) - Date.now();

      if (remaining > 0) {
        // Continue from remaining time
        resetTimer(Math.floor(remaining / 1000));
        startTimer();
        return;
      }
    }

    // No previous expiry → start new 2 min timer
    const expireAt = Date.now() + initialTime * 1000;
    localStorage.setItem("otp_expiry", expireAt.toString());

    startTimer();
  }, []);

  useEffect(() => {
    return () => {
      // 🔥 Clear timer when leaving OTP page
      localStorage.removeItem("otp_expiry");
      resetTimer(120);
      stopTimer();
    };
  }, []);

  const resendOtp = async () => {
    if (timer !== 0) return;

    const email = localStorage.getItem("email");
    if (!email) return;

    try {
      const response = await dispatch<any>(forgotPassword({ email })).unwrap();

      const message = response?.data?.message;
      if (message?.match(emailRegex)?.[0]) {
        setEmail(message?.match(emailRegex)?.[0]);
        setEmailMessage(message);
        return;
      }

      dispatch<any>(
        setToast({
          message: response?.data?.message,
          type: response?.data?.status === 1 ? "success" : "error",
          title: response?.data?.status === 1 ? "Success" : "Error",
        })
      );

      // ⏱ Reset expiry timestamp
      const expireAt = Date.now() + initialTime * 1000;
      localStorage.setItem("otp_expiry", expireAt.toString());

      resetTimer(initialTime);
      startTimer();
    } catch (error) {}
  };

  const handleVerify = async (values: OtpVerifyPayload) => {
    setIsSubmitting(true);

    setLoading(true);
    try {
      const response = await dispatch<any>(otpVerify(values)).unwrap();

      if (response?.data?.status === 1) {
        localStorage.setItem("o_id", response?.data?.o_id);
        dispatch<any>(
          setToast({
            message: response.data.message,
            type: "success",
            title: "Success",
          })
        );

        setLoading(false);
        setTimeout(() => {
          navigate("/reset-password");
        }, 400);
      } else {
        const message = response?.data?.message;
        setLoading(false);
        if (message?.match(emailRegex)?.[0]) {
          setEmail(message?.match(emailRegex)?.[0]);
          setEmailMessage(message);
          return;
        }
        dispatch<any>(
          setToast({
            message: response.data.message,
            type: "error",
            title: "Error",
          })
        );
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      otp: "",
      email: localStorage.getItem("email") || "",
    },
    validationSchema: otpVerifySchema,
    onSubmit: (values) => {
      handleVerify({ otp: Number(values.otp), email: values.email });
    },
  });

  return (
    <div className="form-container">
      <div>
        <Typography
          variant="h1"
          className="logo-title"
          style={{ color: "#05000B", marginBottom: "20px" }}
        >
          OTP Verification
        </Typography>
        <Typography
          variant="h3"
          className="logo-title1"
          style={{ color: "#6C6F71" }}
        >
          Please enter the one-time password (OTP) sent to{" "}
          <span style={{ fontWeight: 600, fontSize: "16px" }}>
            {localStorage.getItem("email")}
          </span>
        </Typography>
      </div>
      {email?.length > 0 && (
        <Box
          sx={{
            textAlign: "start",
            width: 550,
            lineHeight: "24px",
          }}
        >
          <a
            href={`mailto:${email}`}
            style={{
              color: "red",
              fontSize: "16px",
              textAlign: "start",
              textDecoration: "none",
              cursor: "pointer",
              fontWeight: 400,
            }}
          >
            {emailMessage?.replace(`${email}`, "")}
            <span
              style={{
                color: "red",
                textDecoration: "underline",
              }}
            >
              {email}
            </span>
          </a>
        </Box>
      )}
      <form onSubmit={formik.handleSubmit}>
        <FormControl
          variant="standard"
          sx={{ width: "100%", paddingTop: "30px" }}
        >
          <OTPInput
            inputType="tel"
            value={formik.values.otp}
            onChange={(value) => {
              setOtp(value);
              formik.setFieldValue("otp", value);
            }}
            numInputs={4}
            renderSeparator={<span style={{ width: 20 }} />}
            renderInput={(props, index) => (
              <ResponsiveInput
                {...props}
                pattern="[0-9]*"
                onFocus={(e) =>
                  (e.target.style.border = "0.5px solid #A7C7EA ")
                }
                onBlur={(e) => (e.target.style.border = "1px solid #f6f6f6")}
              />
            )}
          />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ marginBottom: "16px" }}
          >
            <div></div>
            <Typography
              color={theme.palette.myCustomColor.link}
              style={{ fontSize: "14px" }}
            ></Typography>
          </Box>

          <CustomButton
            btnTitle={"Submit"}
            type="submit"
            minwidth={"450px"}
            loading={loading}
          />
        </FormControl>
      </form>

      <div style={{ display: "flex", gap: 4, marginTop: "16px" }}>
        <Typography
          style={{
            cursor: timer === 0 ? "pointer" : "not-allowed",
          }}
          color={
            timer === 0
              ? theme.palette.myCustomColor.link
              : theme.palette.myCustomColor.disabled
          }
          onClick={resendOtp}
        >
          Resend OTP{" "}
          {timer !== 0 && (
            <>
              in{" "}
              <span style={{ color: "#2977CC" }}>
                {Math.floor(timer / 60)}:
                {timer % 60 < 10 ? `0${timer % 60}` : timer % 60}
              </span>
            </>
          )}
        </Typography>
      </div>
    </div>
  );
};

export default OtpVerificationForm;
